import {yupResolver} from "@hookform/resolvers/yup";
import {Button, FormControl, FormHelperText, FormLabel, MenuItem, Select, Stack, TextField} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import * as yup from "yup";
import useApiRequest from "../../hooks/useApiRequest";
import {snackbar} from "../../utils/snackbar";
import {PRODUCT_SYNC_TYPE_OPTIONS} from "../../constants/searchFields";

const SYNC_TYPE_VALUES = PRODUCT_SYNC_TYPE_OPTIONS.map((option) => option.value);

const schema = yup.object().shape({
  code: yup
    .string()
    .required("El código es obligatorio")
    .max(255, "Máximo 255 caracteres")
    .matches(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9_ ]*$/, "Se permiten letras, números, espacios y _"),
  name: yup
    .string()
    .required("El nombre es obligatorio")
    .max(255, "Máximo 255 caracteres")
    .matches(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\- ]*$/, "Solo se permiten letras y espacios"),
  description: yup
    .string()
    .required("La descripción es obligatoria")
    .max(255, "Máximo 255 caracteres")
    .matches(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\- ]*$/, "Solo se permiten letras y espacios"),
  sync_type: yup
    .string()
    .required("El tipo de sincronización es obligatorio")
    .oneOf(SYNC_TYPE_VALUES, "Tipo de sincronización inválido")
});

const ProductForm = ({isNew, refreshData, setIsEditing, initialProductData}) => {
  const navigate = useNavigate();

  const {create, update} = useApiRequest({
    baseUrl: `/products`,
    resource: "products"
  });

  const {
    register,
    handleSubmit,
    control,
    formState: {errors}
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      code: initialProductData?.code || "",
      name: initialProductData?.name || "",
      description: initialProductData?.description || "",
      sync_type: initialProductData?.sync_type || ""
    },
    context: {isNew}
  });

  const handleSave = async (data) => {
    const dataToSend = {
      code: data.code,
      name: data.name,
      description: data.description,
      sync_type: data.sync_type
    };

    if (!isNew) {
      dataToSend.id = initialProductData.id;
    }

    const result = isNew ? await create(dataToSend) : await update(initialProductData.id, dataToSend);
    if (!result?.success) return;

    if (isNew) {
      snackbar.success("¡Producto creado correctamente!");
      navigate("/products");
      return;
    }

    snackbar.success("¡Producto actualizado correctamente!");
    refreshData();
    setIsEditing(false);
  };

  const onSubmit = (data) => {
    handleSave(data);
  };

  return (
    <Stack direction="column" spacing={2} minWidth={"300px"}>
      <TextField
        label="Código"
        {...register("code")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.code}
        helperText={errors.code?.message}
        variant="outlined"
        disabled={!isNew}
      />

      <TextField
        label="Nombre"
        {...register("name")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.name}
        helperText={errors.name?.message}
        variant="outlined"
      />

      <TextField
        label="Descripción"
        {...register("description")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.description}
        helperText={errors.description?.message}
        variant="outlined"
      />

      <FormControl fullWidth error={!!errors.sync_type}>
        <FormLabel>Tipo de sincronización *</FormLabel>
        <Controller
          name="sync_type"
          control={control}
          render={({field}) => (
            <Select {...field} variant="outlined" size="small">
              <MenuItem value="" disabled>
                Selecciona
              </MenuItem>
              {PRODUCT_SYNC_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        {errors.sync_type && <FormHelperText>{errors.sync_type.message}</FormHelperText>}
      </FormControl>

      <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
        {isNew ? "Crear" : "Guardar"}
      </Button>
    </Stack>
  );
};

export default ProductForm;
